// *********************************************************
//
// 日付・時刻関連
//
// *********************************************************

import * as moment from 'moment';
import DateTimeFormatOptions = Intl.DateTimeFormatOptions;
import {padZero} from './type';
import {browserLanguage} from './language';

/**
 * UTCオフセット取得処理
 * 戻り値： [-]hh:mm:ss
 */
export const getUtcOffSet = (): string => {
  const d = new Date();
  const offSet = d.getTimezoneOffset();
  let offSetHour = String(Math.floor(Math.abs(offSet) / 60));
  let offSetMinute = String(Math.abs(offSet) % 60);
  let sign = '';
  if (offSet > 0) { // getTimezoneOffsetはUTCより遅い場合にプラスとなる。
    sign = '-';
  }
  if (offSetHour.length === 1) {
    offSetHour = '0' + offSetHour;
  }
  if (offSetMinute.length === 1) {
    offSetMinute = '0' + offSetMinute;
  }

  return sign + offSetHour + ':' + offSetMinute + ':00';
};

/**
 * 時刻生成
 *
 * @param startTime 開始時刻(HH:MM:SS)
 * @param endTime 終了時刻(HH:MM:SS)
 * @param interval 生成間隔(秒)
 * @return 生成した時刻のリスト
 */
export const genTimeList = (startTime: string, endTime: string, interval: number): string[] => {
  const list: string[] = [];
  const cur = new Date('2010/01/01 ' + startTime);
  const end = new Date('2010/01/01 ' + endTime);
  while (cur < end) {
    list.push(cur.toTimeString().substr(0, 8));
    cur.setSeconds(cur.getSeconds() + interval);
  }
  return list;
};

/**
 * Date型を YYYY-MM-DD hh:mm:ss 文字列に変換
 *
 * @param d Date型
 * @return 文字列型
 */
export const date2String = (d: Date) => {
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hour = d.getHours();
  const minute = d.getMinutes();
  const second = d.getSeconds();

  return padZero(year, 4) + '-' + padZero(month, 2) + '-' + padZero(day, 2)
    + ' ' + padZero(hour, 2) + ':' + padZero(minute, 2) + ':' + padZero(second, 2);
};


/**
 * 日付変換処理（年月日時分秒 → 年月日時）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return yyyy-MM-dd hh:00:00
 */
export const getDateHourFromDateTime = (dateTime: string): string => {
  const nengappi = dateTime.substr(0, 13);
  return nengappi + ':00:00';
};

/**
 * 日付変換処理（yyMMdd → 年月日）
 *
 * @param date yyMMdd 形式の文字列
 * @return yyyy-MM-dd 形式の文字列
 */

export const getDateFromShortDate = (date: string): string => {
  const year = date.substr(0, 2);
  const month = date.substr(2, 2);
  const day = date.substr(4, 2);
  return '20' + year + '-' + month + '-' + day;
};

/**
 * 日付変換処理（年月日時分秒 → 年月日時分）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return yyyy-MM-dd hh:m0:00(10分単位)
 */
export const getDate10MinutesFromDateTime = (dateTime: string): string => {
  const nengappi = dateTime.substr(0, 15);
  return nengappi + '0:00';
};


/**
 * 日付変換処理（年月日時分秒 → 年月日）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return yyyy-MM-dd
 */
export const getDateFromDateTime = (dateTime: string): string => dateTime.substr(0, 10);

/**
 * 日付変換処理（年月日時分秒 → 年月日）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return MM/dd
 */
export const getShortDateFromDateTime = (dateTime: string): string => dateTime.substr(0, 10).slice(-5).replace('-', '/');

/**
 * 日付変換処理（年月日時分秒 → 時分秒）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return hh:mm:ss
 */
export const getTimeFromDateTime = (dateTime: string): string | null => {
  if (dateTime.length < 8) {
    return null;
  }
  return dateTime.slice(-8);
};

/**
 * 日付変換処理（年月日時分秒 → 時分）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return hh:mm
 */
export const getShortTimeFromDateTime = (dateTime: string): string | null => {
  if (dateTime.length < 8) {
    return null;
  }
  return dateTime.substr(11, 5);
};

/**
 * 日付変換処理（年月日時分秒 → 時分） baseDateの0時を起点とし、24時を過ぎた場合は25時などと表示。
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @param baseDate yyyy-MM-dd 基準日
 * @return hh:mm
 */
export const getShortTimeFromDateTimeOnBaseDate = (dateTime: string, baseDate: string): string | null => {
  if (dateTime.length < 8) {
    return null;
  }
  // dateTime と baseDate の日付差分を算出
  const dateDiff = getDateDiff(baseDate, getDateFromDateTime(dateTime));

  const hour = Number(dateTime.substr(11, 2)) + 24 * dateDiff;
  return padZero(hour, 2) + dateTime.substr(13, 3);
};

/**
 * 日付変換処理（年月日時分秒 → その週の日曜日の日付）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式 もしくは yyyy-MM-dd 形式の文字列
 * @return yyyy-MM-dd形式。その週の日曜の日付
 */
export const getSundayFromDateTime = (dateTime: string): string => {

  // 0埋め用の関数
  const dateZellFill = (num: string | number) => ('0' + num)
      .substr(-2);

  // 週初の日曜を取得
  const date2 = convertDateStringToObject(dateTime.substr(0, 10));
  const weekNumber = date2.getDay(); // 曜日（Number）の取得。0=日曜
  date2.setDate(date2.getDate() - weekNumber); // 日曜日の日付をセット

  // 日曜の日付を格納
  return date2.getFullYear() + '-' + dateZellFill((date2.getMonth() + 1)) + '-' +
    dateZellFill(date2.getDate());
};


/**
 * 日付変換処理（年月日時分秒 → その週の土曜日の日付）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式 もしくは yyyy-MM-dd 形式の文字列
 * @return yyyy-MM-dd形式。その週の土曜の日付
 */
export const getSaturdayFromDateTime = (dateTime: string) => {

  // 0埋め用の関数
  const dateZellFill = (num: string | number) => ('0' + num)
      .substr(-2);

  // 週初の日曜を取得
  const date2 = convertDateStringToObject(dateTime.substr(0, 10));
  const weekNumber = date2.getDay(); // 曜日（Number）の取得。0=日曜
  date2.setDate(date2.getDate() + 6 - weekNumber); // 土曜日の日付をセット

  // 土曜の日付を格納
  return date2.getFullYear() + '-' + dateZellFill((date2.getMonth() + 1)) + '-' +
    dateZellFill(date2.getDate());
};

/**
 * 日付変換処理（年月日時分秒 → その月の1日の日付）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式 もしくは yyyy-MM-dd 形式の文字列
 * @return yyyy-MM-01
 */
export const get1stDayFromDateTime = (dateTime: string): string => dateTime.substr(0, 7) + '-01';

/**
 * 日付変換処理（年月日時分秒 → その月の最終日の日付）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式 もしくは yyyy-MM-dd 形式の文字列
 * @return yyyy-MM-dd形式。その月の最終日
 */
export const getLastDayFromDateTime = (dateTime: string): string => {

  // 0埋め用の関数
  const dateZellFill = (num: string | number) => ('0' + num)
      .substr(-2);

  // 月末日付を取得
  const date2 = convertDateStringToObject(dateTime.substr(0, 10));
  date2.setMonth(date2.getMonth() + 1);
  date2.setDate(0);

  // 月末日付を格納
  return date2.getFullYear() + '-' + dateZellFill((date2.getMonth() + 1)) + '-' +
    dateZellFill(date2.getDate());
};

/**
 * 日付変換処理（年月日時分秒 → 指定された分単位で丸めた時刻）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @param roundMinute 数値
 * @return yyyy-MM-dd hh:mm:00 mmは、第二引数で指定された分で切り捨てた値。
 */
export const getRoundedMinuteFromDateTime = (dateTime: string, roundMinute: number): string => {

  // 0埋め用の関数
  const dateZellFill = (num: string | number) => ('0' + num)
      .substr(-2);

  const yyyymmddhh = dateTime.substr(0, 13);
  const min = Math.floor(Number(dateTime.substr(14, 2)) / roundMinute) * roundMinute;

  return yyyymmddhh + ':' + dateZellFill(min) + ':00';
};

/**
 * 日付変換処理（年月日時分秒 → 指定された分単位で丸めた時刻　時刻（時分）のみ）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式の文字列
 * @param roundMinute 数値
 * @return hh:mm mmは、第二引数で指定された分で切り捨てた値。
 */
export const getRoundedHourMinuteFromDateTime = (dateTime: string, roundMinute: number): string => {

  // 0埋め用の関数
  const dateZellFill = (num: string | number) => ('0' + num)
      .substr(-2);

  const hh = dateTime.substr(11, 2);
  const min = Math.floor(Number(dateTime.substr(14, 2)) / roundMinute) * roundMinute;

  return hh + ':' + dateZellFill(min);
};

/**
 * 日付変換処理（ミリ秒 → yyyy/mm-dd hh:mm）
 *
 * @param milliTimestamp yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return yyyy/MM/dd hh:mm 形式の文字列
 */
export const convertMilliTimestampToYMDHM = (milliTimestamp: number): string => {
  //milliTimestampをdate型に変換
  const date = new Date(milliTimestamp);
  // yyyy/MM/dd hh:mm 形式へ変換
  const ymdhm = date.getFullYear() + '/' + ('0' + (date.getMonth() + 1)).slice(-2) + '/' +
    ('0' + date.getDate()).slice(-2) + ' ' + ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2);
  return ymdhm;
};

/**
 * 日付変換処理（ミリ秒 → yyyy-MM-dd）
 *
 * @param milliTimestamp yyyy-MM-dd hh:mm:ss 形式の文字列
 * @return yyyy-MM-dd 形式の文字列
 */
export const convertMilliTimestampToYMD = (milliTimestamp: number): string => {
  //milliTimestampをdate型に変換
  const date = new Date(milliTimestamp);
  // yyyy-MM-dd 形式へ変換
  const ymd =
    date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
  return ymd;
};

/**
 * 日付変換処理（エポック形式）
 *
 * @param epoch 1970/01/01 00:00:00 UTC からの経過ミリ秒
 * @param roundMinute 数値
 * @return 第一引数を第二引数(分)で切り捨てた値。
 */
export const getRoundedEpochMillis = (epoch: number, roundMinute: number): number => Math.floor(epoch / (roundMinute * 60 * 1000)) * roundMinute * 60 * 1000;

/**
 * yyyy-MM-dd または yyyy-MM-dd HH:mm:ss または yyyy-MM-dd HH:mm:ss.sss 形式文字列を Date オブジェクトに変換する。
 *
 * @param date yyyy-MM-dd または yyyy-MM-dd HH:mm:ss または yyyy-MM-dd HH:mm:ss.sss 形式文字列
 * @return Dateオブジェクト
 */
export const convertDateStringToObject = (date: string): Date => {
  const year = date.substring(0, 4);
  const month = date.substring(5, 7);
  const day = date.substring(8, 10);
  if (date.length < 19) { // yyyy-MM-dd 形式
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  } else {
    const hour = date.substring(11, 13);
    const min = date.substring(14, 16);
    const sec = date.substring(17, 19);
    if (date.length < 23) { // yyyy-MM-dd HH:mm:ss 形式
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10),
        parseInt(hour, 10), parseInt(min, 10), parseInt(sec, 10));
    } else {
      const ms = date.substring(20, 23);
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10),
        parseInt(hour, 10), parseInt(min, 10), parseInt(sec, 10), parseInt(ms, 10));
    }
  }
};

/**
 * エポック形式の時刻を Date オブジェクトに変換する。
 *
 * @param epoch 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒
 * 10桁以内ならば秒、11桁以上ならばミリ秒とみなす。
 */
export const convertEpochToObject = (epoch: number): Date => {
  if (epoch <= 9999999999) { // 10桁以内ならば秒とみなす。
    epoch = epoch * 1000;
  }
  return new Date(epoch);
};

/**
 * YYYY-MM-DD 形式文字列を YYYYMMDD に変換する
 *
 * @param date YYYY-MM-DD 形式
 * @return YYYYMMDD 形式。引数が YYYY-MM-DD 形式でなければ引数そのままを返す。
 */
export const convertHyphenyyyymmddToyyyymmdd = (date: string | undefined | null): string | undefined | null => {
  if (typeof date === 'undefined') {
    return undefined;
  }
  if (date === null) {
    return null;
  }

  const match = date.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
  if (match === null || match.length !== 4) {
    return date;
  }
  const year = match[1];
  const month = match[2];
  const day = match[3];
  return year + month + day;

};

/**
 * 2つの日付の差分を取得
 *
 * @param date1 yyyy-MM-dd 形式文字列
 * @param date2 yyyy-MM-dd 形式文字列
 * @return date2 と date1 の日数差分。date2 が後のときが正。
 */
export const getDateDiff = (date1: string, date2: string): number => {
  // let d1 = new Date(date1.replace(/-/g, "/"));
  // let d2 = new Date(date2.replace(/-/g, "/"));
  const d1 = convertDateStringToObject(date1);
  const d2 = convertDateStringToObject(date2);

  const msDiff = d2.getTime() - d1.getTime();

  return Math.floor(msDiff / (1000 * 60 * 60 * 24));
};

/**
 * 2つの日時の差分を取得
 *
 * @param dt1 yyyy-MM-dd HH:mm:ss 形式文字列
 * @param dt2 yyyy-MM-dd HH:mm:ss 形式文字列
 * @return dt2 と dt1 の秒数差分。dt2 が後のときが正。
 */
export const getDateTimeDiff = (dt1: string, dt2: string): number => {
  // let d1 = new Date(date1.replace(/-/g, "/"));
  // let d2 = new Date(date2.replace(/-/g, "/"));
  const d1 = convertDateStringToObject(dt1);
  const d2 = convertDateStringToObject(dt2);

  const msDiff = d2.getTime() - d1.getTime();

  return Math.floor(msDiff / 1000);
};

/**
 * 00:00:00 からの経過秒数を取得
 *
 * @param time HH:mm:ss 形式文字列
 * @return 00:00:00 からの経過秒数
 */
export const genSecInDay = (time: string): number => {
  const hour = time.substring(0, 2);
  const min = time.substring(3, 5);
  const sec = time.substring(6, 8);

  return parseInt(hour, 10) * 3600 + parseInt(min, 10) * 60 + parseInt(sec, 10);
};

/**
 * 2つの時刻の差分を取得
 *
 * @param time1 HH:mm:ss 形式文字列
 * @param time2 HH:mm:ss 形式文字列
 * @return time2 と time1 の秒数差分。time2 が後のときが正。
 */
export const getTimeDiff = (time1: string, time2: string): number => genSecInDay(time2) - genSecInDay(time1);


/**
 * 日付加算
 *
 * @param date yyyy-MM-dd 形式文字列
 * @param diff 数値
 * @return date から diff 日後の日付(yyyy-MM-dd形式文字列)。diff がマイナスの場合は前の日付
 */
export const addDate = (date: string, diff: number): string => {
  const d1 = convertDateStringToObject(date);
  d1.setDate(d1.getDate() + diff);
  const retval = getDateFromDateTime(date2String(d1));
  if (typeof retval === 'undefined' || retval === null) {
    return '';
  }
  return retval;
};

/**
 * 秒数加算
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式文字列
 * @param diff 数値
 * @return dateTime から diff 秒後の日時(yyyy-MM-dd HH:mm:ss 形式文字列)。diff がマイナスの場合は前の日時
 */
export const addSeconds = (dateTime: string, diff: number): string => {
  const d1 = convertDateStringToObject(dateTime);
  d1.setSeconds(d1.getSeconds() + diff);
  const retval = date2String(d1);
  if (typeof retval === 'undefined' || retval === null) {
    return '';
  }
  return retval;
};

/**
 * 日付を国際表記で表現
 *
 * @param value 日付。YYYY-MM-DD 形式文字列 or 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒 or Date型 or Moment型
 * @param locale
 * @return locale に合わせた日付表現(文字列型)
 */
export const intlDateWithLocale = (value: any, locale: string): string => {
  let d: Date;
  if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}/.test(value)) {
    d = convertDateStringToObject(value);
  } else if (typeof value === 'number') {
    d = convertEpochToObject(value);
  } else if (value instanceof Date) {
    d = value;
  } else if (moment.isMoment(value)) {
    d = value.toDate();
  } else {
    return value;
  }

  const options: DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  return new Intl.DateTimeFormat(locale, options).format(d);
};

/**
 * 日付を国際表記で表現
 *
 * @param value 日付。YYYY-MM-DD 形式文字列 or 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒 or Date型 or Moment型
 * @return ブラウザの locale に合わせた日付表現(文字列型)
 */
export const intlDate = (value: any): string => {
  const locale = browserLanguage();
  return intlDateWithLocale(value, locale);
};

/**
 * MM/DDを国際表記で表現
 *
 * @param value 日付。YYYY-MM-DD 形式文字列 or 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒 or Date型 or Moment型
 * @param locale
 * @return locale に合わせた日付表現(文字列型)
 */
export const intlMmDdWithLocale = (value: any, locale: string): string => {
  let d: Date;
  if (typeof value === 'string' && /\d{4}-\d{2}-\d{2}/.test(value)) {
    d = convertDateStringToObject(value);
  } else if (typeof value === 'number') {
    d = convertEpochToObject(value);
  } else if (value instanceof Date) {
    d = value;
  } else if (moment.isMoment(value)) {
    d = value.toDate();
  } else {
    return value;
  }

  const options: DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
  };

  return new Intl.DateTimeFormat(locale, options).format(d);
};

/**
 * MM/DDを国際表記で表現
 *
 * @param value 日付。YYYY-MM-DD 形式文字列 or 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒 or Date型 or Moment型
 * @return ブラウザの locale に合わせた日付表現(文字列型)
 */
export const intlMmDd = (value: any): string => {
  const locale = browserLanguage();
  return intlMmDdWithLocale(value, locale);
};

/**
 * 日時を国際表記で表現
 *
 * @param value 日付。YYYY-MM-DD hh:mm:ss 形式文字列 or 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒 or Date型 or Moment型
 * @param locale
 * @return locale に合わせた日付表現(文字列型)
 */
export const intlDateTimeWithLocale = (value: any, locale: string) => {
  let d: Date;
  if (typeof value === 'string' && /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(value)) {
    d = convertDateStringToObject(value);
  } else if (typeof value === 'number') {
    d = convertEpochToObject(value);
  } else if (value instanceof Date) {
    d = value;
  } else if (moment.isMoment(value)) {
    d = value.toDate();
  } else {
    return value;
  }

  const options: DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };

  return new Intl.DateTimeFormat(locale, options).format(d);
};

/**
 * 日時を国際表記で表現
 *
 * @param value 日付。YYYY-MM-DD hh:mm:ss 形式文字列 or 1970/01/01 00:00:00 UTC からの経過ミリ秒 もしくは 経過秒 or Date型 or Moment型
 * @return ブラウザの locale に合わせた日付表現(文字列型)
 */
export const intlDateTime = (value: any): string => {
  const locale = browserLanguage();
  return intlDateTimeWithLocale(value, locale);
};

/**
 * 日付変換処理（年月日時分秒 → ミリ秒）
 *
 * @param dateTime yyyy-MM-dd hh:mm:ss 形式
 * @return ミリ秒
 */
export const convertToMilliSeconds = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.getTime();
};

/**
 * 指定した日付が今日以降かを確認
 */
export const isAfterToday = (dateStr: string, includeToday: boolean): boolean => {
  const todayStr = getDateFromDateTime(date2String(new Date()));//今日日付
  if (includeToday) {
    return dateStr >= todayStr;
  } else {
    return dateStr > todayStr;
  }
};

/**
 * 指定した日付が今日日付かを確認
 */
export const isToday = (dateStr: string): boolean => {
  const todayStr = getDateFromDateTime(date2String(new Date()));//今日日付
  return todayStr === dateStr;
};
